import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import TextField from "../../../components/Form/TextField";
import Select from "../../../components/Form/Select";
import IconButton from "../../../components/Buttons/IconButton";
import { useTranslationApp } from "../../../lib/i18next";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import { generateRandomPromotionalCode } from "../promotionalCodes/utils/promotionalCode";
import {
  PROMOTIONAL_CODE_TYPE_APP_SUMO,
  PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT,
  PROMOTIONAL_CODE_DISCOUNT_TYPE_FIXED,
  PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE,
} from "../../../actions/owner";
import {
  BUTTON_CANCEL,
  BUTTON_SAVE,
  ERROR_OCCURRED,
  FIELD_REQUIRED,
  OWNER_PROMOTIONAL_CODES_CREATE_SUCCESS,
  OWNER_PROMOTIONAL_CODES_CREATE_TITLE,
  OWNER_PROMOTIONAL_CODES_ERROR_DUPLICATE,
  OWNER_PROMOTIONAL_CODES_ERROR_INVALID,
  OWNER_PROMOTIONAL_CODES_ERROR_INVALID_STRIPE_DISCOUNT,
  OWNER_PROMOTIONAL_CODES_COLUMN_TYPE,
  OWNER_PROMOTIONAL_CODES_FIELD_CODE,
  OWNER_PROMOTIONAL_CODES_FIELD_MAX_REDEMPTIONS,
  OWNER_PROMOTIONAL_CODES_TYPE_VALUE_APP_SUMO,
  OWNER_PROMOTIONAL_CODES_TYPE_VALUE_STRIPE_DISCOUNT,
  OWNER_PROMOTIONAL_CODES_GENERATE_CODE_TOOLTIP,
  OWNER_PROMOTIONAL_CODES_MODAL_SUBTITLE,
  OWNER_PROMOTIONAL_CODES_FIELD_TIER,
  OWNER_PROMOTIONAL_CODES_TIER_OPTION_1,
  OWNER_PROMOTIONAL_CODES_TIER_OPTION_2,
  OWNER_PROMOTIONAL_CODES_TIER_OPTION_3,
  OWNER_PROMOTIONAL_CODES_FIELD_STRIPE_CODE,
  OWNER_PROMOTIONAL_CODES_FIELD_DISCOUNT,
  OWNER_PROMOTIONAL_CODES_FIELD_DISCOUNT_TYPE,
  OWNER_PROMOTIONAL_CODES_DISCOUNT_TYPE_FIXED,
  OWNER_PROMOTIONAL_CODES_DISCOUNT_TYPE_PERCENTAGE,
} from "../../../i18n/keysTranslations";
import useFocusErrorForm from "../../../hooks/useFocusErrorForm";

const PROMOTIONAL_CODE_TYPE_CREATE_OPTIONS = [
  {
    value: PROMOTIONAL_CODE_TYPE_APP_SUMO,
    label: OWNER_PROMOTIONAL_CODES_TYPE_VALUE_APP_SUMO,
  },
  {
    value: PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT,
    label: OWNER_PROMOTIONAL_CODES_TYPE_VALUE_STRIPE_DISCOUNT,
  },
];

const APP_SUMO_TIER_SELECT_OPTIONS = [
  { value: 1, label: OWNER_PROMOTIONAL_CODES_TIER_OPTION_1 },
  { value: 2, label: OWNER_PROMOTIONAL_CODES_TIER_OPTION_2 },
  { value: 3, label: OWNER_PROMOTIONAL_CODES_TIER_OPTION_3 },
];

const DISCOUNT_TYPE_SELECT_OPTIONS = [
  {
    value: PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE,
    label: OWNER_PROMOTIONAL_CODES_DISCOUNT_TYPE_PERCENTAGE,
  },
  {
    value: PROMOTIONAL_CODE_DISCOUNT_TYPE_FIXED,
    label: OWNER_PROMOTIONAL_CODES_DISCOUNT_TYPE_FIXED,
  },
];

const DEFAULT_FORM = {
  code: "",
  maxRedemptions: 1,
  type: PROMOTIONAL_CODE_TYPE_APP_SUMO,
  tier: 1,
  stripeCode: "",
  discount: "",
  discountType: PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE,
};

const ModalCreatePromotionalCode = ({
  modalOpen = false,
  onCloseModal = () => {},
  onCallbackSave = async () => {},
}) => {
  const { t } = useTranslationApp();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, handleSubmit, reset, setValue, formState } = useForm({
    defaultValues: DEFAULT_FORM,
    mode: "onSubmit",
  });

  const selectedType = useWatch({ control, name: "type" });
  const showAppSumoTierSelect = selectedType === PROMOTIONAL_CODE_TYPE_APP_SUMO;
  const showStripeDiscountFields =
    selectedType === PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT;

  const { errors } = formState;

  useFocusErrorForm(formState);

  useEffect(() => {
    if (!modalOpen) {
      reset(DEFAULT_FORM);
    }
  }, [modalOpen, reset]);

  const onGenerateCode = () => {
    setValue("code", generateRandomPromotionalCode(), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      await onCallbackSave({
        code: values.code,
        maxRedemptions: values.maxRedemptions,
        type: values.type,
        tier: values.tier,
        stripeCode: values.stripeCode,
        discount: values.discount,
        discountType: values.discountType,
      });
      await SimpleAlert({
        title: t(OWNER_PROMOTIONAL_CODES_CREATE_SUCCESS),
        icon: "success",
      });
      reset(DEFAULT_FORM);
      onCloseModal();
    } catch (e) {
      const codeErr = e?.message || e?.code;
      let message = t(ERROR_OCCURRED);
      if (codeErr === "DUPLICATE_CODE") {
        message = t(OWNER_PROMOTIONAL_CODES_ERROR_DUPLICATE);
      } else if (codeErr === "INVALID_CODE") {
        message = t(OWNER_PROMOTIONAL_CODES_ERROR_INVALID);
      } else if (codeErr === "INVALID_STRIPE_DISCOUNT") {
        message = t(OWNER_PROMOTIONAL_CODES_ERROR_INVALID_STRIPE_DISCOUNT);
      }
      await SimpleAlert({
        title: message,
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(OWNER_PROMOTIONAL_CODES_CREATE_TITLE)}
      description={t(OWNER_PROMOTIONAL_CODES_MODAL_SUBTITLE)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        <TextField
          fullWidth
          name="code"
          label={t(OWNER_PROMOTIONAL_CODES_FIELD_CODE)}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  tooltipText={OWNER_PROMOTIONAL_CODES_GENERATE_CODE_TOOLTIP}
                  onClick={onGenerateCode}
                  disabled={isSubmitting}
                  edge="end"
                >
                  <AutorenewIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />

        <TextField
          fullWidth
          name="maxRedemptions"
          label={t(OWNER_PROMOTIONAL_CODES_FIELD_MAX_REDEMPTIONS)}
          type="number"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            min: {
              value: 1,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />

        <Select
          control={control}
          name="type"
          label={t(OWNER_PROMOTIONAL_CODES_COLUMN_TYPE)}
          options={PROMOTIONAL_CODE_TYPE_CREATE_OPTIONS}
          doTranslate
          fullWidth
          variant="filled"
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />

        {showAppSumoTierSelect ? (
          <Select
            control={control}
            name="tier"
            label={t(OWNER_PROMOTIONAL_CODES_FIELD_TIER)}
            options={APP_SUMO_TIER_SELECT_OPTIONS}
            doTranslate
            fullWidth
            variant="filled"
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
            }}
            errors={errors}
          />
        ) : null}

        {showStripeDiscountFields ? (
          <>
            <TextField
              fullWidth
              name="stripeCode"
              label={t(OWNER_PROMOTIONAL_CODES_FIELD_STRIPE_CODE)}
              variant="filled"
              color="primary"
              InputLabelProps={{ shrink: true }}
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
              }}
              errors={errors}
            />

            <Select
              control={control}
              name="discountType"
              label={t(OWNER_PROMOTIONAL_CODES_FIELD_DISCOUNT_TYPE)}
              options={DISCOUNT_TYPE_SELECT_OPTIONS}
              doTranslate
              fullWidth
              variant="filled"
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
              }}
              errors={errors}
            />

            <TextField
              fullWidth
              name="discount"
              label={t(OWNER_PROMOTIONAL_CODES_FIELD_DISCOUNT)}
              type="number"
              variant="filled"
              color="primary"
              InputLabelProps={{ shrink: true }}
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
                min: {
                  value: 0.01,
                  message: t(FIELD_REQUIRED),
                },
              }}
              errors={errors}
            />
          </>
        ) : null}

        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            loading: isSubmitting,
            onClick: handleSubmit(onSubmit),
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalCreatePromotionalCode;
